import { Fragment } from 'react';

import { cn } from './cn';

/**
 * THE SKELETON VOCABULARY — the parts every surface's pending state is built
 * from.
 *
 * WHY PARTS RATHER THAN ONE PAGE-WIDE SKELETON. A placeholder earns its place
 * only when it stands where the real content will stand: a poster grid under a
 * route that renders a table, or a table under a route that renders a form,
 * moves the layout twice — once to the wrong shape and once back — and that
 * second move is exactly the layout shift the placeholder exists to prevent.
 * `router/PendingRoute.tsx` is therefore the FLOOR, and each surface whose
 * shape differs from it composes its own `<Page>.Skeleton.tsx` from the pieces
 * below at its own real geometry (`docs/v1/PRD-TDD.md` §«Loading»: a skeleton
 * matching the loaded geometry, never a bare spinner on a surface whose shape
 * is known).
 *
 * NOTHING HERE IS ANNOUNCED. Every part is `aria-hidden`: a placeholder has no
 * text to read, and a screen reader that walks a dozen empty boxes learns less
 * than one that hears the surface's own single polite announcement. The surface
 * owns that announcement; these are pixels only.
 *
 * THE SHEEN IS A SWEEP, NOT A BLINK. `animate-pulse` fades a whole block in and
 * out, which under this palette reads as flicker; a single travelling highlight
 * over a static base is calmer at the same information content. Under
 * `prefers-reduced-motion` the global stylesheet flattens every animation to a
 * single frame, leaving the base block — still the right shape, no movement.
 */
export function Skeleton({ className }: { readonly className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative overflow-hidden rounded-sm bg-skeleton-base',
        // The sweep travels toward the READING END, which under `rtl` is the
        // left — the same direction the eye leaves a line in.
        'after:absolute after:inset-0 after:animate-skeleton-sheen after:bg-gradient-to-l',
        'after:from-transparent after:via-skeleton-sheen after:to-transparent',
        className,
      )}
    />
  );
}

/** A run of text lines. The last one is short, because a paragraph's last line
 *  is — a stack of equal bars reads as a table, not as prose. */
export function SkeletonLines({
  lines = 3,
  className,
}: {
  readonly lines?: number;
  readonly className?: string;
}) {
  return (
    <div className={cn('grid gap-3', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-4', index === lines - 1 ? 'w-1/2' : index % 3 === 1 ? 'w-11/12' : 'w-full')}
        />
      ))}
    </div>
  );
}

/** The breadcrumb trail, the page heading and its lede — the head every inner
 *  surface in this area opens with, at the rhythm the real head keeps. */
export function SkeletonHead({ withLede = true }: { readonly withLede?: boolean }) {
  return (
    <div className="my-5 mb-4">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="mt-5 h-9 w-2/3 max-w-[26rem]" />
      {withLede ? <Skeleton className="mt-4 h-4 w-1/2 max-w-[34rem]" /> : null}
    </div>
  );
}

/**
 * One horizontal shelf: its heading row and the cards that peek off the
 * reading-end edge.
 *
 * The column ladder is the RAIL's own — 2.5 · 4.5 · 6.5 · 8 posters, 1.2 · 2.2
 * · 3.2 · 4 landscape cards — rather than a rounder count invented here. A
 * placeholder rail that fits a different number of cards than the loaded one
 * hands the reader a different layout twice.
 */
export function SkeletonRail({
  ratio = 'wide',
  count,
}: {
  readonly ratio?: 'poster' | 'wide';
  readonly count?: number;
}) {
  // The count DEFAULTS TO THE WIDEST TIER's own column count — 8 posters, 4
  // landscape cards — rather than to a round number. `grid-auto-columns` sizes
  // every column for that tier whether or not a card was drawn into it, so a
  // rail of six posters on a desktop viewport leaves two full columns of empty
  // track at the reading end: a placeholder that ends before the row does,
  // which is the one thing a rail must never look like.
  const cards = count ?? (ratio === 'wide' ? 4 : 8);

  return (
    <section aria-hidden="true" className="my-6">
      <div className="mb-3 flex items-baseline gap-3">
        <Skeleton className="h-6 w-40" />
        {/* The rail's own «عرض الكلّ», which sits at the reading end of the
            heading row wherever the source has a listing of its own. */}
        <Skeleton className="ms-auto h-4 w-16" />
      </div>

      <div
        className={cn(
          'grid grid-flow-col gap-rail-gap overflow-hidden',
          ratio === 'wide'
            ? '[grid-auto-columns:calc((100%-1*var(--rail-gap))/1.2)] md:[grid-auto-columns:calc((100%-2*var(--rail-gap))/2.2)] lg:[grid-auto-columns:calc((100%-3*var(--rail-gap))/3.2)] xl:[grid-auto-columns:calc((100%-3*var(--rail-gap))/4)]'
            : '[grid-auto-columns:calc((100%-2*var(--rail-gap))/2.5)] md:[grid-auto-columns:calc((100%-4*var(--rail-gap))/4.5)] lg:[grid-auto-columns:calc((100%-6*var(--rail-gap))/6.5)] xl:[grid-auto-columns:calc((100%-7*var(--rail-gap))/8)]',
        )}
      >
        {Array.from({ length: cards }, (_, index) => (
          <Skeleton
            key={index}
            className={cn('w-full rounded-md', ratio === 'wide' ? 'aspect-[16/9]' : 'aspect-[2/3]')}
          />
        ))}
      </div>
    </section>
  );
}

/** A grid of title cards — the shape of every membership listing in the area. */
export function SkeletonPosterGrid({ count = 6 }: { readonly count?: number }) {
  return (
    <div
      aria-hidden="true"
      className="mt-5 grid grid-cols-2 gap-x-rail-gap gap-y-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
    >
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className="aspect-[16/9] w-full" />
      ))}
    </div>
  );
}

/**
 * The wide card grid — `[data-grid="wide"]`, the 1 · 2 · 2 · 2 · 3 ladder the
 * collection cards ride. `cover` draws the member strip a collection card opens
 * with; a card whose real head is text alone passes `cover={false}` rather than
 * reserving a band that never arrives.
 */
export function SkeletonCardGrid({
  count = 3,
  cover = true,
}: {
  readonly count?: number;
  readonly cover?: boolean;
}) {
  return (
    <div
      aria-hidden="true"
      className="my-5 grid grid-cols-1 gap-4 gap-x-rail-gap sm:grid-cols-2 xl:grid-cols-3"
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="overflow-hidden rounded-md border border-border bg-surface">
          {cover ? <Skeleton className="aspect-[21/9] w-full rounded-none" /> : null}
          <div className="p-4">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="mt-3 h-4 w-1/2" />
            <Skeleton className="mt-3 h-4 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Table rows at the REAL column count, inside the same bordered wrapper the
 * loaded table sits in.
 *
 * `columns` is the loaded table's own count and `widths` its own rhythm: the
 * first cell carries the row's name and takes the free space, and the rest are
 * fixed. A placeholder at a different column count reflows the table on
 * arrival, which is this surface's entire CLS budget.
 */
export function SkeletonTable({
  columns,
  rows = 8,
  widths,
  minWidthClass,
}: {
  readonly columns: number;
  readonly rows?: number;
  readonly widths?: readonly string[];
  /** The loaded table's own `min-inline-size`, so the placeholder scrolls where
   *  the table scrolls instead of fitting a width the table never has. */
  readonly minWidthClass?: string;
}) {
  return (
    <div aria-hidden="true" className="tbl-wrap rounded-md border border-border bg-surface">
      <div className={minWidthClass}>
        {Array.from({ length: rows }, (_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex items-center gap-4 border-b border-border px-4 py-4 last:border-b-0"
          >
            {Array.from({ length: columns }, (_, columnIndex) => (
              <Skeleton
                key={columnIndex}
                className={cn(
                  'h-4 rounded-xs',
                  widths?.[columnIndex] ?? (columnIndex === 0 ? 'flex-1' : 'w-24'),
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A bordered panel at a stated height — the card, the facts grid, the plan
 *  tile: anything whose loaded shape is one filled surface rather than a run of
 *  distinguishable lines. */
export function SkeletonPanel({ className }: { readonly className?: string }) {
  return <Skeleton className={cn('w-full rounded-md', className)} />;
}

/** Label-and-control pairs, at the control's real `44px` minimum — the shape
 *  every settings form collapses into. */
export function SkeletonFields({ rows = 4 }: { readonly rows?: number }) {
  return (
    <div aria-hidden="true" className="grid gap-6">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="grid gap-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-11 w-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * The account head panel — portrait, display name, handle, biography line and
 * the one action beside them. Both profile surfaces open on it, and it is the
 * one head in the area whose portrait is a circle rather than a rectangle, so
 * the placeholder is a `rounded-pill` at the same three tier sizes.
 */
export function SkeletonAccountHead({ withAction = true }: { readonly withAction?: boolean }) {
  return (
    <div
      aria-hidden="true"
      className="my-5 mb-7 grid gap-4 rounded-md border border-border bg-surface p-5 shadow-elev-1 md:gap-5 md:p-6"
    >
      <div className="flex items-center gap-4">
        <Skeleton className="h-[4.5rem] w-[4.5rem] shrink-0 rounded-pill md:h-[5.5rem] md:w-[5.5rem] lg:h-[7rem] lg:w-[7rem]" />
        <div className="min-w-0 flex-1">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="mt-2 h-4 w-28" />
        </div>
      </div>
      <Skeleton className="h-4 w-full max-w-measure" />
      {withAction ? <Skeleton className="h-11 w-40" /> : null}
    </div>
  );
}

/**
 * The home billboard, at the hero's own box AND its own contents.
 *
 * A hero is the tallest single block in the area — `min-h-[30rem]` from the
 * tablet tier and `min-h-[38rem]` from the desktop one — so a featureless
 * rectangle of that height is the largest lie a placeholder can tell: it
 * reserves the right amount of space and states nothing about what will stand
 * in it. The blocks below are the slide's own furniture in the slide's own
 * order — the kicker, the title, the meta row, the three synopsis lines, the
 * two actions and the pager — so the swap moves nothing and the reader can see
 * a hero coming rather than a grey wall.
 *
 * The base tier stacks art over copy and the tablet tier overlays them, exactly
 * as `Billboard` does, because that is the one breakpoint at which the hero
 * changes shape.
 */
export function SkeletonBillboard() {
  return (
    <section
      aria-hidden="true"
      className="relative mt-5 overflow-hidden rounded-lg bg-surface md:flex md:min-h-[30rem] md:items-end lg:min-h-[38rem]"
    >
      <Skeleton className="aspect-[16/9] w-full rounded-none md:absolute md:inset-0 md:aspect-auto md:h-full" />

      <div className="relative p-5 md:max-w-[34rem] md:p-6">
        {/* The kicker — «فيلم» / «مسلسل», the served content type. */}
        <Skeleton className="mb-2 h-3 w-16" />
        <Skeleton className="h-10 w-3/4 max-w-[22rem]" />
        <Skeleton className="my-3 h-4 w-2/3 max-w-[18rem]" />

        <div className="mb-4 grid gap-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-2/3" />
        </div>

        {/* Both actions, at the action row's real `3.25rem` and its real
            full-width-then-auto behaviour. */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <Skeleton className="h-[3.25rem] w-full rounded-sm sm:w-40" />
          <Skeleton className="h-[3.25rem] w-full rounded-sm sm:w-40" />
        </div>
      </div>

      {/* The pager, where the pager sits. Three dots is not a claim about the
          slide count — it is the smallest run that reads AS a pager, and the
          row is `justify-center` at the base tier so a different count does not
          move anything beside it. */}
      <div className="flex items-center justify-center gap-2 pt-3 md:absolute md:bottom-4 md:end-6 md:z-[2] md:pt-0">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-10 w-10 rounded-sm" />
        ))}
      </div>
    </section>
  );
}

/**
 * Label-and-value pairs inside a fact list, on the list's OWN two-track grid.
 *
 * `columnsClass` is the loaded `<dl>`'s own template rather than one restated
 * here: the subscription panels ride a `10rem` / `12rem` label column that
 * collapses to one column on a phone, and the support facts ride
 * `max-content`. A placeholder on the wrong template puts every value in the
 * wrong place and then moves it.
 *
 * The label and its value are SIBLINGS in the grid, exactly as `<dt>` and
 * `<dd>` are: wrapping each pair would take the value out of the value column
 * and the two tracks would stop aligning down the list.
 */
export function SkeletonFactRows({
  rows,
  columnsClass = 'grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)] lg:grid-cols-[12rem_minmax(0,1fr)]',
}: {
  readonly rows: number;
  readonly columnsClass?: string;
}) {
  return (
    <div aria-hidden="true" className={cn('grid gap-x-4 gap-y-2 text-sm', columnsClass)}>
      {Array.from({ length: rows }, (_, index) => (
        <Fragment key={index}>
          <Skeleton className="h-4 w-24" />
          <Skeleton className={cn('h-4', index % 3 === 1 ? 'w-2/3' : 'w-1/2')} />
        </Fragment>
      ))}
    </div>
  );
}

/**
 * A panel with a head band and a fact list — the shape both subscription
 * surfaces open on: a title, the state or kind chip at the reading end of the
 * band, and the facts beneath it.
 *
 * The chip's box IS reserved. A grey block states that something stands there
 * and never which state it is, and the alternative — leaving the slot empty —
 * reflows the head band's baseline the moment the badge lands, which is the one
 * row on these pages where a shift is unmissable.
 */
export function SkeletonFactPanel({
  rows,
  columnsClass,
  withTotal = false,
}: {
  readonly rows: number;
  readonly columnsClass?: string;
  /** The band the loaded panel closes on — the total row, where it has one. */
  readonly withTotal?: boolean;
}) {
  return (
    <section aria-hidden="true" className="overflow-hidden rounded-md border border-border bg-surface">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-editorial-soft p-4 md:px-5">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="ms-auto h-6 w-20 rounded-pill" />
      </div>

      <div className="p-4">
        <SkeletonFactRows rows={rows} columnsClass={columnsClass} />

        {withTotal ? (
          <div className="mt-5 grid gap-1 rounded-md border-s-[3px] border-s-editorial bg-editorial-soft p-4 sm:flex sm:items-baseline sm:gap-3">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-8 w-32 sm:ms-auto" />
          </div>
        ) : null}
      </div>
    </section>
  );
}
